import React, { useEffect, useRef } from 'react';
import { flameSoftSprite, drawGasBlob } from '../../utils/emberPalette';

// "Galaxy Collapse" intro: spiral arms of gas orbit inward and are absorbed
// into the always-visible head, charging it with light until a rim-light flash.
const HeadAssembly = ({ onDone }) => {
  const canvasRef = useRef(null);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = canvas.offsetWidth;
    const h = canvas.offsetHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const headEl = document.querySelector('.xp-head');
    if (!headEl) {
      doneRef.current && doneRef.current();
      return;
    }
    const cb = canvas.getBoundingClientRect();
    const hb = headEl.getBoundingClientRect();
    const hx = hb.left + hb.width / 2 - cb.left;
    const hy = hb.top + hb.height / 2 - cb.top;
    const rx = hb.width * 0.22;
    const ry = hb.height * 0.34;

    // ambient nebula clouds drifting with the collapse
    const P = window.innerWidth <= 768 ? 12 : 24;
    const puffs = Array.from({ length: P }, () => {
      const ang = Math.random() * Math.PI * 2;
      const dist = 130 + Math.random() * 330;
      return {
        ang,
        dist,
        size: 90 + Math.random() * 160,
        spin: 0.002 + Math.random() * 0.0022,
        drift: 0.14 + Math.random() * 0.2,
        tw: Math.random() * Math.PI * 2,
        tws: 0.008 + Math.random() * 0.014,
      };
    });

    // accretion ring removed (was drawing a visible oval over the head)

    let flare = 0;
    let raf;
    const t0 = performance.now();

    const softEraseHead = () => {
      // feathered erase over the head so gas fades out with no hard oval edge
      ctx.save();
      ctx.globalCompositeOperation = 'destination-out';
      ctx.translate(hx, hy);
      ctx.scale(1, ry / rx);
      const g = ctx.createRadialGradient(0, 0, 0, 0, 0, rx * 1.18);
      g.addColorStop(0, 'rgba(0, 0, 0, 1)');
      g.addColorStop(0.68, 'rgba(0, 0, 0, 0.92)');
      g.addColorStop(1, 'rgba(0, 0, 0, 0)');
      ctx.beginPath();
      ctx.arc(0, 0, rx * 1.18, 0, Math.PI * 2);
      ctx.fillStyle = g;
      ctx.fill();
      ctx.restore();
    };

    const tick = () => {
      const now = performance.now();
      const elapsed = now - t0;
      const fadeIn = Math.min(elapsed / 700, 1);
      ctx.clearRect(0, 0, w, h);
      ctx.globalCompositeOperation = 'lighter';

      const energy = Math.min(elapsed / 3200, 1);
      const flareT = flare ? (now - flare) / 500 : 0;
      const fade = flare ? Math.max(0, 1 - flareT) : 1;

      // Act 1+: nebula haze
      for (const q of puffs) {
        q.ang += q.spin;
        if (q.dist > 100) q.dist -= q.drift;
        q.tw += q.tws;
        const qx = hx + Math.cos(q.ang) * q.dist;
        const qy = hy + Math.sin(q.ang) * q.dist * 0.8;
        const qa = (0.055 + Math.sin(q.tw) * 0.03) * fadeIn * fade;
        drawGasBlob(ctx, flameSoftSprite(Math.min(q.dist / 450, 1)), qx, qy, q.size, q.ang + Math.PI / 2, 1.8, Math.max(qa, 0));
      }

      // Act 3: rim-light ignition flash around the silhouette
      if (flare) {
        const fa = Math.sin(Math.min(flareT, 1) * Math.PI) * 0.5;
        if (fa > 0.01) {
          const g2 = ctx.createRadialGradient(hx, hy, rx * 0.7, hx, hy, rx * 2.2);
          g2.addColorStop(0, `rgba(255, 196, 128, ${fa})`);
          g2.addColorStop(0.45, `rgba(190, 120, 255, ${fa * 0.5})`);
          g2.addColorStop(1, 'rgba(120, 140, 255, 0)');
          ctx.beginPath();
          ctx.arc(hx, hy, rx * 2.2, 0, Math.PI * 2);
          ctx.fillStyle = g2;
          ctx.fill();
        }
      }
      softEraseHead();

      // the head charges up as it absorbs the galaxy
      const glow = flare ? 1 + Math.sin(Math.min(flareT, 1) * Math.PI) * 0.18 : 0.62 + 0.38 * energy;
      headEl.style.filter = `brightness(${glow.toFixed(3)}) saturate(${(0.65 + 0.35 * energy).toFixed(3)})`;

      const complete = elapsed > 3200;
      if (complete && !flare) flare = now;
      if (flare && flareT > 1.2) {
        headEl.style.filter = '';
        doneRef.current && doneRef.current();
        return;
      }
      raf = requestAnimationFrame(tick);
    };
    tick();

    return () => {
      cancelAnimationFrame(raf);
      headEl.style.filter = '';
    };
  }, []);

  return <canvas ref={canvasRef} className="xp-assembly" aria-hidden="true" data-testid="xp-assembly" />;
};

export default HeadAssembly;
